import { createFileRoute } from "@tanstack/react-router";

import { LmHero } from "../components/lombard-marketing/LmHero";
import { LmLinks } from "../components/lombard-marketing/LmLinks";
import { LmPromise } from "../components/lombard-marketing/LmPromise";
import { LmResults, type LmResultGroup } from "../components/lombard-marketing/LmResults";
import { LmScope } from "../components/lombard-marketing/LmScope";
import { LmCardGrid } from "../components/lombard-marketing/LmSections";
import { LmStory } from "../components/lombard-marketing/LmStory";
import {
  analyticsCards,
  auditCards,
  needCards,
  salesCards,
  strategyCards,
  systemCards,
} from "../components/lombard-marketing/content";
import { Faq } from "../components/shared/Faq";
import { LeadForm } from "../components/shared/LeadForm";
import { ServicePageLayout } from "../components/site/ServicePageLayout";
import { crumbTuples } from "../lib/breadcrumbs";
import { routes } from "../lib/nav";
import { breadcrumbJsonLd, buildMeta, serviceJsonLd, SITE_URL } from "../lib/site";

const title = "Продвижение и маркетинг ломбарда под ключ — кейс HAY.agency";
const description =
  "Продвижение ломбарда: Яндекс Карты, контекстная реклама, сайт, CRM и сквозная аналитика. Показываем подход и подтверждённые результаты кейса.";

const confirmedResultGroups: LmResultGroup[] = [
  {
    title: "Расчёт эффективности кампаний",
    period: "прогноз на дату публикации кейса",
    source: "внутренняя финансовая модель проекта",
    items: [
      { value: "1 467 789 ₽", label: "затраченный бюджет" },
      { value: "7 346 891 ₽", label: "планируемый доход с каналов продвижения" },
      { value: "415%", label: "рентабельность по методике проекта" },
    ],
  },
  {
    title: "Результаты реального проекта",
    period: "2024 год",
    source: "внутренняя отчётность проекта",
    items: [
      { value: "967 895 ₽", label: "потрачено на маркетинг" },
      { value: "4 678 345 ₽", label: "планируемый доход с каналов продвижения" },
      { value: "234%", label: "рентабельность маркетинга и отдела продаж" },
    ],
  },
  {
    title: "Операционные показатели проекта",
    period: "на дату публикации кейса",
    source: "CRM и внутренняя отчётность проекта",
    items: [
      { value: "15 567 ₽", label: "средний чек сделки" },
      { value: "3 489", label: "обработанных заявок" },
      { value: "3 006 457 ₽", label: "выручка на дату публикации кейса" },
    ],
  },
];

const faqItems = [
  {
    q: "С чего начинается продвижение ломбарда?",
    a: "Начинаем с аудита: проверяем карточки филиалов, рекламу, сайт, CRM, телефонию и доступную аналитику. После этого определяем приоритеты, состав работ и показатели, по которым будем оценивать изменения.",
  },
  {
    q: "Какие доступы понадобятся?",
    a: "Набор зависит от задачи. Обычно нужны согласованные права к рекламным кабинетам, карточкам в геосервисах, Яндекс Метрике, CRM и телефонии. Доступы запрашиваем только к тем системам, которые входят в работу.",
  },
  {
    q: "Можно ли продвигать сеть с несколькими филиалами?",
    a: "Да. Для сети задаём единый стандарт данных, разделяем рекламу по географии и сравниваем показатели филиалов по согласованной методике. Приоритеты определяем отдельно для каждой точки.",
  },
  {
    q: "Как вы измеряете результат?",
    a: "Сопоставляем данные рекламных кабинетов, сайта, звонков и CRM. Рекламные показы и переходы не считаем продажами: бизнес-результат оцениваем только по данным, которые подтверждены в системах клиента.",
  },
  {
    q: "Можно заказать только продвижение в Картах?",
    a: "Да. Если задача ограничена Яндекс Картами и 2ГИС, можно начать с отдельного направления. Связку с сайтом, рекламой и CRM предлагаем только тогда, когда она действительно нужна для результата и измерения обращений.",
  },
  {
    q: "С какой CRM вы работаете?",
    a: "Выбор CRM зависит от процессов и уже используемых систем. Работаем с amoCRM, RetailCRM, Битрикс24 и другими решениями, если у них есть необходимые возможности интеграции.",
  },
  {
    q: "Учитываете ли вы ограничения рекламы ломбардов?",
    a: "Да, при подготовке кампаний учитываем действующие требования рекламных площадок и перед запуском согласуем формулировки с клиентом. Юридическую экспертизу и правовое заключение при необходимости проводит профильный специалист на стороне клиента.",
  },
];

export const Route = createFileRoute("/lombard-marketing")({
  head: () => {
    const base = buildMeta({
      title,
      description,
      path: routes.lombardMarketing,
      image: `${SITE_URL}/og/services/lombard-marketing.webp`,
      imageWidth: 1200,
      imageHeight: 630,
      imageAlt: "Маркетинг и продвижение ломбарда — HAY.agency",
      locale: "ru_RU",
    });

    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebPage",
                "@id": `${SITE_URL}${routes.lombardMarketing}#webpage`,
                url: `${SITE_URL}${routes.lombardMarketing}`,
                name: title,
                description,
                inLanguage: "ru-RU",
                isPartOf: { "@id": `${SITE_URL}/#website` },
                breadcrumb: {
                  "@id": `${SITE_URL}${routes.lombardMarketing}#breadcrumbs`,
                },
                mainEntity: {
                  "@id": `${SITE_URL}${routes.lombardMarketing}#service`,
                },
              },
              serviceJsonLd({
                name: "Маркетинг и продвижение ломбарда",
                description,
                path: routes.lombardMarketing,
                id: `${SITE_URL}${routes.lombardMarketing}#service`,
                serviceType: "Комплексный маркетинг ломбарда",
                providerId: `${SITE_URL}/#organization`,
                inGraph: true,
              }),
              breadcrumbJsonLd(crumbTuples(routes.lombardMarketing), {
                id: `${SITE_URL}${routes.lombardMarketing}#breadcrumbs`,
                inGraph: true,
              }),
              {
                "@type": "FAQPage",
                "@id": `${SITE_URL}${routes.lombardMarketing}#faq`,
                mainEntity: faqItems.map((item) => ({
                  "@type": "Question",
                  name: item.q,
                  acceptedAnswer: { "@type": "Answer", text: item.a },
                })),
              },
            ],
          }),
        },
      ],
    };
  },
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout breadcrumb="Маркетинг ломбарда" jsonLd={false}>
      <LmHero />

      <LmCardGrid
        id="lm-needs"
        title="Когда нужен"
        accent="комплексный маркетинг"
        lead="Комплексный подход полезен, когда отдельные каналы уже не дают целостной картины или сеть готовится к масштабированию."
        cards={needCards}
      />

      <LmStory
        title="Что проверяем"
        accent="на старте"
        lead="Разбираем рекламу, конкурентную среду, аудиторию и путь обращения, чтобы определить задачи до запуска работ."
        cards={auditCards}
      />

      <LmCardGrid
        dark
        id="lm-system"
        eyebrow="Каналы и система"
        title="Связываем путь клиента"
        accent="от показа до сделки"
        lead="Каналы могут работать отдельно, но для управляемого результата нужны единые источники данных и понятные границы измерения."
        cards={systemCards}
      />

      <LmStory
        eyebrow="Привлечение"
        title="Разрабатываем"
        accent="маркетинговую стратегию"
        lead="Группируем спрос, настраиваем кампании, проверяем гипотезы и регулярно оптимизируем рекламу по качеству обращений."
        cards={strategyCards}
      />

      <LmStory
        dark
        title="Выстраиваем"
        accent="обработку обращений"
        lead="Помогаем зафиксировать этапы работы с лидами, убрать ручные потери и собрать коммуникации в CRM."
        cards={salesCards}
      />

      <LmResults groups={confirmedResultGroups} />
      <LmPromise />
      <LmScope />

      <LmStory
        dark
        eyebrow="Управленческий учёт"
        title="Собираем управленческую"
        accent="аналитику по согласованным источникам"
        lead="Формируем отчётность только по тем данным, которые доступны и корректно передаются из рекламы, сайта, телефонии и CRM."
        cards={analyticsCards}
      />

      <Faq
        compact
        items={faqItems}
        title="Частые вопросы о маркетинге ломбарда"
        lead="Коротко о старте, доступах, аналитике и продвижении сети филиалов."
      />

      <LmLinks />

      <LeadForm
        id="lombard-audit"
        title="Получите аудит маркетинга ломбарда"
        lead="Обсудим текущие каналы, филиалы и систему аналитики. После знакомства предложим состав работ без обещаний результата до изучения данных."
        submitLabel="Получить аудит"
      />
    </ServicePageLayout>
  );
}
